import React, { useEffect, useState } from "react";
import { useAuth } from "../auth/AuthContext";
import { apiGet } from "../../shared/apiClient";

function StatCard({ label, value, sub, gradient }) {
  return (
    <div
      className={`group relative overflow-hidden rounded-2xl border border-white/20 bg-gradient-to-br ${gradient} p-6 text-white shadow-xl shadow-slate-900/10 transition hover:shadow-2xl hover:shadow-slate-900/15 md:p-7`}
    >
      <div className="pointer-events-none absolute -right-6 -top-6 h-32 w-32 rounded-full bg-white/10 blur-2xl transition group-hover:bg-white/15" />
      <div
        className="pointer-events-none absolute -bottom-4 left-1/2 h-24 w-24 -translate-x-1/2 rounded-full bg-black/5 blur-xl"
        aria-hidden
      />
      <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-white/75">{label}</p>
      <p className="mt-2 text-4xl font-bold tabular-nums tracking-tight md:text-[2.75rem]">{value}</p>
      {sub && <p className="mt-2 text-sm text-white/80">{sub}</p>}
    </div>
  );
}

export function DashboardPage() {
  const { user } = useAuth();
  const isAdmin = String(user?.role || "").toLowerCase() === "admin";
  const [data, setData] = useState({ customers: 0, blogs: 0, visits: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = async () => {
    setLoading(true);
    setError("");
    const res = await apiGet("/api/Dashboard/summary");
    if (res.ok) {
      setData({
        customers: res.data.customers ?? 0,
        blogs: res.data.blogs ?? 0,
        visits: res.data.visits ?? 0
      });
    } else {
      setError(res.error || "Failed to load");
    }
    setLoading(false);
  };

  useEffect(() => {
    void load();
  }, []);

  const firstName = (user?.userName || "there").split(/[@\s]/)[0];
  const hour = new Date().getHours();
  const greet = hour < 12 ? "Good morning" : hour < 18 ? "Good afternoon" : "Good evening";

  return (
    <div className="w-full">
      <div className="relative overflow-hidden rounded-3xl border border-slate-200/80 bg-gradient-to-br from-slate-900 via-slate-800 to-emerald-950 p-7 text-white shadow-2xl shadow-slate-900/25 md:p-9">
        <div className="pointer-events-none absolute -right-20 -top-20 h-64 w-64 rounded-full bg-emerald-500/20 blur-3xl" />
        <div className="pointer-events-none absolute -bottom-16 left-1/3 h-48 w-48 rounded-full bg-teal-400/10 blur-3xl" />
        <div className="relative">
          <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-emerald-300/80">Dashboard</p>
          <h1 className="mt-1 text-2xl font-semibold tracking-tight md:text-3xl">
            {greet}, {firstName}
          </h1>
          <p className="mt-2 max-w-lg text-sm text-slate-300">
            {isAdmin
              ? "High-level view of your organizations and published content. Metrics update when you open this page."
              : "Track your content footprint and company profile from one place."}
          </p>
        </div>
      </div>

      {error && (
        <p className="mt-6 text-sm text-red-600" role="alert">
          {error}
        </p>
      )}

      {loading ? (
        <div className="mt-8 flex items-center gap-3 text-slate-500">
          <span
            className="inline-block h-5 w-5 animate-spin rounded-full border-2 border-emerald-200 border-t-emerald-600"
            aria-hidden
          />
          Loading…
        </div>
      ) : (
        <div className={`mt-8 grid w-full gap-5 ${isAdmin ? "sm:grid-cols-3" : "sm:grid-cols-2"}`}>
          {isAdmin && (
            <StatCard
              label="Customers"
              value={data.customers}
              sub="Active company accounts in the system"
              gradient="from-emerald-600 via-emerald-700 to-teal-800"
            />
          )}
          <StatCard
            label="Blog posts"
            value={data.blogs}
            sub={isAdmin ? "Total articles across all accounts" : "Your published and draft articles"}
            gradient="from-violet-600 via-indigo-700 to-slate-900"
          />
          <StatCard
            label="Website visits"
            value={data.visits}
            sub={isAdmin ? "Total tracked visitor events" : "Tracked visits for your website"}
            gradient="from-cyan-600 via-blue-700 to-slate-900"
          />
        </div>
      )}
    </div>
  );
}
